import React from 'react'
import reactStringReplace from 'react-string-replace'
import { Chart } from './Chart'
import { ChartTypesEnum } from '../config/indicators-model'

type Props = {
    html: string;
}

/** Marcadores del tipo {chart id="C31" type="Area" year="2020" hide="E451,E452"} */
const CHART_REGEX = /\{chart\s+([^{}]*)\}/gi

/** Etiquetas que se admiten en el contenido editado desde el backoffice. */
const ETIQUETAS_PERMITIDAS = [
    'H2', 'H3', 'H4', 'H5', 'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S',
    'UL', 'OL', 'LI', 'A', 'BLOCKQUOTE', 'SUB', 'SUP', 'SPAN', 'IMG',
]

/** Atributos que se conservan (el resto, incluidos estilos y on*, se eliminan). */
const ATRIBUTOS_PERMITIDOS: { [etiqueta: string]: string[] } = {
    A: ['href', 'title'],
    IMG: ['src', 'alt', 'title'],
}

const URL_SEGURA = /^(https?:\/\/|\/|mailto:|#)/i

export type Marcador = {
    id: string;
    type?: string;
    year?: number;
    hide?: string[];
}

/**
 * Lee los atributos de un marcador de grafico.
 * Devuelve null si no trae id (marcador invalido).
 */
export const leerMarcador = (atributos: string): Marcador | null => {
    const id = atributos.match(/id\s*=\s*["']([^"']+)["']/i)
    if (!id) {
        return null
    }

    const type = atributos.match(/type\s*=\s*["']([^"']+)["']/i)
    const year = atributos.match(/year\s*=\s*["'](\d{4})["']/i)
    const hide = atributos.match(/hide\s*=\s*["']([^"']+)["']/i)

    return {
        id: id[1],
        type: type ? type[1] : undefined,
        year: year ? Number(year[1]) : undefined,
        hide: hide ? hide[1].split(',').map(s => s.trim()).filter(Boolean) : undefined,
    }
}

/**
 * El codigo de la categoria es el prefijo del indicador: E45 -> E4, C31 -> C3.
 */
export const categoriaDe = (id: string): string => id.substring(0, 2)

/**
 * Deja el html en lo permitido: fuera etiquetas desconocidas (conservando su
 * texto), atributos de estilo y cualquier on*. Sin dependencias nuevas.
 */
export const limpiarHtml = (html: string): string => {
    if (typeof DOMParser === 'undefined' || !html) {
        return html
    }

    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
    const raiz = doc.body.firstElementChild
    if (!raiz) {
        return ''
    }

    const revisar = (nodo: Element) => {
        Array.from(nodo.children).forEach(hijo => {
            if (!ETIQUETAS_PERMITIDAS.includes(hijo.tagName)) {
                hijo.replaceWith(...Array.from(hijo.childNodes))
                return
            }

            const permitidos = ATRIBUTOS_PERMITIDOS[hijo.tagName] || []
            Array.from(hijo.attributes).forEach(atributo => {
                const nombre = atributo.name.toLowerCase()
                const esUrl = nombre === 'href' || nombre === 'src'
                if (!permitidos.includes(nombre) || (esUrl && !URL_SEGURA.test(atributo.value))) {
                    hijo.removeAttribute(atributo.name)
                }
            })

            revisar(hijo)
        })
    }
    revisar(raiz)

    return raiz.innerHTML
}

const tipoDeChart = (tipo?: string): ChartTypesEnum | undefined => {
    if (!tipo) {
        return undefined
    }
    const clave = Object.keys(ChartTypesEnum).find(k => k.toLowerCase() === tipo.toLowerCase())
    return clave ? ChartTypesEnum[clave as keyof typeof ChartTypesEnum] : undefined
}

/**
 * Pinta el html de un bloque de "De un vistazo": el html tal cual (ya limpio) y
 * los marcadores convertidos en graficos de verdad. Mismo enfoque que el cuerpo
 * de los articulos.
 */
export const OverviewContent: React.FC<Props> = ({ html }) => {
    // El editor puede escapar las comillas de los marcadores.
    const contenido = (html || '').replace(/&quot;/g, '"').replace(/&#39;/g, "'")

    const partes = contenido.match(CHART_REGEX)
        ? reactStringReplace(contenido, CHART_REGEX, (marcador, i) => {
            const datos = leerMarcador(marcador)

            if (!datos) {
                return null
            }

            return (
                <Chart
                    key={`chart-${i}`}
                    chart={categoriaDe(datos.id)}
                    subChart={datos.id}
                    defaultChartType={tipoDeChart(datos.type)}
                    defaultYear={datos.year}
                    disableSeries={datos.hide}
                />
            )
        })
        : [contenido]

    return (
        <>
            {partes.map((parte, i) =>
                typeof parte === 'object' ? (
                    <React.Fragment key={`parte-${i}`}>{parte}</React.Fragment>
                ) : (
                    <div key={`texto-${i}`} dangerouslySetInnerHTML={{ __html: limpiarHtml(String(parte)) }} />
                ),
            )}
        </>
    )
}
